import React, { useState, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Upload, Eye, Play, AlertTriangle, CheckCircle2, XCircle, FileText, Trash2, Gift } from 'lucide-react';
import { toast } from 'sonner';

const SAMPLE = `2025-07-15,VMR,ICICIBANK,100,11,Final
2025-08-20,Bhanu,TCS,50,25,Interim
2025-09-10,Indira,HDFCBANK,200,19.5,Final`;

const formatINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const parseInput = (text) => {
  const rows = [];
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  lines.forEach((line, idx) => {
    if (idx === 0 && /^(date|sl|s\.no|sno)/i.test(line)) return;
    const parts = line.split(/[\t,]/).map(p => p.trim());
    if (parts.length < 5) return;
    rows.push({
      date: parts[0],
      member: parts[1],
      symbol: parts[2],
      quantity: parts[3],
      dividend_per_share: parts[4],
      type: parts[5] || 'Interim',
      financial_year: parts[6] || '',
      notes: parts[7] || '',
    });
  });
  return rows;
};

const BulkImportDividends = ({ user, setUser }) => {
  const [text, setText] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const parsedRows = useMemo(() => parseInput(text), [text]);

  const handlePreview = useCallback(async () => {
    if (!parsedRows.length) {
      toast.error('Please paste at least one dividend row');
      return;
    }
    try {
      setLoading(true);
      const res = await api.post('/dividends/bulk', { rows: parsedRows, dry_run: true });
      setPreviewData(res.data);
      const { ok, errors } = res.data.summary;
      if (errors > 0) toast.error(`${errors} row(s) have errors`);
      else toast.success(`${ok} row(s) ready to apply`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [parsedRows]);

  const handleApply = useCallback(async () => {
    if (!previewData || previewData.summary.errors > 0) {
      toast.error('Fix errors before applying');
      return;
    }
    if (!window.confirm(`Apply ${previewData.summary.ok} dividend record(s)?`)) return;

    try {
      setApplying(true);
      const res = await api.post('/dividends/bulk', { rows: parsedRows, dry_run: false });
      setPreviewData(res.data);
      toast.success(`Applied ${res.data.created || res.data.summary.ok} dividend(s) successfully`);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [previewData, parsedRows]);

  const loadSample = () => setText(SAMPLE);
  const clearAll = () => { setText(''); setPreviewData(null); };

  const renderRow = (r) => {
    const statusColor = r.status === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700';
    const StatusIcon = r.status === 'error' ? XCircle : CheckCircle2;
    const isApplied = previewData?.applied;
    const inp = r.input;
    const res = r.resolved;

    return (
      <tr key={r.index} className="border-b hover:bg-gray-50" data-testid={`row-${r.index}`}>
        <td className="px-2 py-2 text-center">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
            <StatusIcon size={12} /> {isApplied ? '✓ Applied' : r.status.toUpperCase()}
          </span>
        </td>
        <td className="px-2 py-2 text-sm">{inp.date}</td>
        <td className="px-2 py-2 text-sm">{res?.member_name || inp.member}</td>
        <td className="px-2 py-2 text-sm font-medium">{res?.symbol || inp.symbol}</td>
        <td className="px-2 py-2 text-right text-sm">{Number(inp.quantity).toLocaleString('en-IN')}</td>
        <td className="px-2 py-2 text-right text-sm">₹{Number(inp.dividend_per_share).toFixed(2)}</td>
        <td className="px-2 py-2 text-right text-sm font-semibold text-green-700">
          {res ? formatINR(res.total_amount) : '—'}
        </td>
        <td className="px-2 py-2 text-sm">{res?.financial_year || inp.financial_year || '—'}</td>
        <td className="px-2 py-2 text-sm">
          <span className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
            {res?.dividend_type || inp.type || 'Interim'}
          </span>
        </td>
        <td className="px-2 py-2 text-xs text-gray-500 max-w-xs">
          {r.messages?.length > 0 ? r.messages.join('; ') : (inp.notes || '')}
        </td>
      </tr>
    );
  };

  const summary = previewData?.summary;
  const canApply = previewData && !previewData.applied && summary?.errors === 0 && summary?.ok > 0;

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="bulk-dividends-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <Gift className="text-emerald-600" size={28} /> Bulk Dividend Import
          </h1>
          <p className="text-gray-600 text-sm">
            Paste CSV/TSV rows. Each line: <code className="text-xs bg-gray-100 px-1 rounded">Date, Member, Symbol, Qty, Dividend/Share, [Type], [FY], [Notes]</code>
            {' '}· Date format: YYYY-MM-DD or DD-MM-YYYY · FY auto-derived if blank.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><Upload size={16} /> Paste rows</h3>
              <div className="flex gap-2">
                <button onClick={loadSample} className="text-xs text-indigo-600 hover:underline" data-testid="load-sample-btn">Load sample</button>
                <button onClick={clearAll} className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1" data-testid="clear-btn">
                  <Trash2 size={12} /> Clear
                </button>
              </div>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="2025-07-15,VMR,ICICIBANK,100,11,Final&#10;2025-08-20,Bhanu,TCS,50,25,Interim"
              className="w-full h-64 font-mono text-sm input-field resize-none"
              data-testid="bulk-textarea"
            />
            <div className="mt-2 text-xs text-gray-500">
              {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} detected
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handlePreview}
                disabled={loading || !parsedRows.length}
                className="btn-primary flex items-center gap-1 text-sm disabled:opacity-50"
                data-testid="preview-btn"
              >
                <Eye size={14} /> {loading ? 'Validating...' : 'Preview'}
              </button>
              <button
                onClick={handleApply}
                disabled={!canApply || applying}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 disabled:opacity-40"
                data-testid="apply-btn"
              >
                <Play size={14} /> {applying ? 'Applying...' : 'Apply All'}
              </button>
            </div>
          </div>

          <div className="card p-4">
            {summary ? (
              <div data-testid="summary-card">
                <h3 className="font-semibold mb-3">
                  Validation Summary {previewData.applied && <span className="text-green-600 text-sm">(Applied ✓)</span>}
                </h3>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Total rows</p>
                    <p className="text-xl font-bold">{summary.total}</p>
                  </div>
                  <div className="p-2 bg-green-50 rounded">
                    <p className="text-xs text-gray-500">Ready</p>
                    <p className="text-xl font-bold text-green-700">{summary.ok}</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded">
                    <p className="text-xs text-gray-500">Errors</p>
                    <p className="text-xl font-bold text-red-700">{summary.errors}</p>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <p className="text-xs text-emerald-700 uppercase tracking-wider">Total Dividend Amount</p>
                  <p className="text-2xl font-bold text-emerald-800">{formatINR(summary.total_amount)}</p>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText size={16} /> Format Guide</h3>
                <div className="text-sm space-y-2 text-gray-600">
                  <p><strong>Required columns (in order):</strong></p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li><strong>Date</strong> — Dividend date (YYYY-MM-DD or DD-MM-YYYY)</li>
                    <li><strong>Member</strong> — Holder name (e.g. VMR, Bhanu, Indira)</li>
                    <li><strong>Symbol</strong> — Stock symbol (e.g. ICICIBANK, TCS)</li>
                    <li><strong>Qty</strong> — Shares held on record date</li>
                    <li><strong>Dividend/Share</strong> — ₹ per share</li>
                    <li><strong>Type</strong> (optional) — Interim / Final / Special (default: Interim)</li>
                    <li><strong>FY</strong> (optional) — e.g. FY26 (auto-derived from date if blank)</li>
                    <li><strong>Notes</strong> (optional) — any free text</li>
                  </ol>
                  <p className="mt-3"><strong>Notes:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Total amount = Qty × Dividend/Share</li>
                    <li>Indian FY: Apr-Mar. FY26 = Apr 2025 - Mar 2026.</li>
                    <li>You can paste CSV/TSV directly from Excel/Google Sheets</li>
                    <li>First row can be a header (auto-skipped)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {previewData && (
          <div className="card overflow-hidden" data-testid="preview-table-card">
            <div className="p-3 bg-gray-50 border-b">
              <h3 className="font-semibold">Row Preview</h3>
            </div>
            <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-center font-semibold">Status</th>
                    <th className="px-2 py-2 text-left font-semibold">Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Member</th>
                    <th className="px-2 py-2 text-left font-semibold">Symbol</th>
                    <th className="px-2 py-2 text-right font-semibold">Qty</th>
                    <th className="px-2 py-2 text-right font-semibold">DPS</th>
                    <th className="px-2 py-2 text-right font-semibold">Total</th>
                    <th className="px-2 py-2 text-left font-semibold">FY</th>
                    <th className="px-2 py-2 text-left font-semibold">Type</th>
                    <th className="px-2 py-2 text-left font-semibold">Notes / Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map(renderRow)}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BulkImportDividends;
